import { useCallback, useEffect, useRef } from 'react';
import { useAtomValue, useUpdateAtom } from 'jotai/utils';
import {
  chatHistoryViewAtom,
  chatMessagesAtom,
  chatSessionIdAtom,
  chatSessionResetRequestAtom,
  chatSessionScheduleIdAtom,
  chatStreamingAtom,
  chatTurnIdAtom,
} from 'src/shared/chat';

type ResetDeps = {
  /**
   * Cancels the in-flight turn. Must be the `stop` from the SAME
   * `useChatSend` instance that sent it — the AbortController is per-instance,
   * so a `stop` from anywhere else silently does nothing.
   */
  stopStream: () => void;
  /** Persists whatever the auto-save debounce still owes. */
  flushSave: () => void;
};

/**
 * Performs the "start a new agent session" request signalled by
 * `chatSessionResetRequestAtom`.
 *
 * Mount this exactly once, in the component that owns the sender and the
 * auto-save — today that is ChatSidebar. Producers (the header button, the
 * nudge pills) only bump the atom.
 *
 * Order is the whole point:
 *
 *  1. **Cancel the live turn first.** The proxy persists the partial output
 *     and the local fetch aborts. Skipping this would leave a stream writing
 *     into what the user now sees as a new conversation.
 *  2. **Flush the auto-save.** It debounces 2s and its effect bails out on an
 *     empty message list, so clearing first means a turn finished inside that
 *     window is never saved — the conversation would be gone from history
 *     instead of merely set aside.
 *  3. **Then clear**, including the session-scoped labels (`schedule_id`) that
 *     `MessageList` reads, or a fresh session keeps rendering as a schedule
 *     run.
 *
 * Not cleared: `chatSessionOriginAtom`. A nudge writes the origin for the
 * session it is about to start, and this hook runs after that write.
 *
 * A straggling stream cannot pollute the new session: every write in
 * `useSSEStream` after the opening append is a `prev.map` keyed on its own
 * message id, so once the list is empty they all no-op.
 */
export function useChatSessionReset({ stopStream, flushSave }: ResetDeps) {
  const resetRequest = useAtomValue(chatSessionResetRequestAtom);
  const messages = useAtomValue(chatMessagesAtom);
  const sessionId = useAtomValue(chatSessionIdAtom);
  const isStreaming = useAtomValue(chatStreamingAtom);

  const setMessages = useUpdateAtom(chatMessagesAtom);
  const setSessionId = useUpdateAtom(chatSessionIdAtom);
  const setTurnId = useUpdateAtom(chatTurnIdAtom);
  const setSessionScheduleId = useUpdateAtom(chatSessionScheduleIdAtom);
  const setHistoryView = useUpdateAtom(chatHistoryViewAtom);

  const startNewSession = useCallback(() => {
    // Already fresh — no turn to cancel, nothing to save, nothing to clear.
    // Worth the check: a pill clicked on an empty panel is the common case,
    // and this keeps it from issuing a pointless save request.
    if (messages.length === 0 && !sessionId) {
      setHistoryView(false);
      return;
    }
    if (isStreaming) stopStream();
    flushSave();
    setMessages([]);
    setSessionId(null);
    setTurnId(null);
    setSessionScheduleId(null);
    setHistoryView(false);
  }, [
    messages.length,
    sessionId,
    isStreaming,
    stopStream,
    flushSave,
    setMessages,
    setSessionId,
    setTurnId,
    setSessionScheduleId,
    setHistoryView,
  ]);

  // Seeded with the current value so mounting never resets: producers bump the
  // counter, and only a CHANGE is a request. (The atom is module-level state,
  // so on a remount it may already be non-zero.)
  const handledRef = useRef(resetRequest);

  useEffect(() => {
    if (resetRequest === handledRef.current) return;
    handledRef.current = resetRequest;
    startNewSession();
  }, [resetRequest, startNewSession]);

  return startNewSession;
}

export default useChatSessionReset;
